import { BARBARIAN, CLERIC, DRUID, FORKS, GROVE, HALL, LAND_LEVELS, NIGHTMARES, PALADIN, SKILLS, SKILL_FX, SKILL_RANK_BP, TEMPLE, UNDEAD, UNIQUE_FORMS } from '@somnhold/content';
import { panoplyHit } from './panoply.ts';
import { hexToWorld } from '../hex.ts';
import { idiv, imax, imin, mulBp, withinRange } from '../math/fixed.ts';
import { rankAt, skillLevelBp } from './create.ts';
import type { BattleState, Enemy, Hero, Structure } from '../types.ts';
import { damageEnemy, enemiesNear, healHero, isFlying as isFlyingNow, targetable } from './combat.ts';
import { choirHealed } from './panoply-cleric.ts';
import { isBoss } from './towers.ts';
import { preyFuryBp, second as secondShare, secondDevotionBp, tally } from './legacy.ts';

/**
 * The kits of the barbarian, the paladin and the druid that live outside their skills: passives,
 * frenzy and bear form (they change the hero's stats while they last), shields.
 */

// ---------- barbarian ----------

/** Fury: the barbarian hits harder the more life it is missing (+2 % per 3 %, up to the cap at a quarter of life). */
export const furyBp = (h: Hero, e?: Enemy): number => {
  if (h.cls !== 'barbarian') return 10000;
  // The long hunt (the barbarian's legacy): on the prey, the fury is at its full.
  if (e && h.lgPrey === e.id) return preyFuryBp();
  if (h.stats.maxHp <= 0 || h.hp >= h.stats.maxHp) return 10000;
  const missing = idiv((h.stats.maxHp - h.hp) * 10000, h.stats.maxHp);
  return 10000 + imin(BARBARIAN.fury.maxBp, idiv(missing * 2, 3));
};

/** The frenzy (and the barbarian's fury) drink life from what it deals. */
export const lifesteal = (s: BattleState, h: Hero, dealt: number, e?: Enemy): void => {
  if ((h.frenzy ?? 0) > 0 && dealt > 0) healHero(s, h, mulBp(dealt, BARBARIAN.frenzy.lifestealBp), h.pid);
  // Fury drinks too: the more life missing, the more of the blow comes back (all of drinkBp at full fury).
  const fury = furyBp(h, e) - 10000;
  if (fury > 0 && dealt > 0) {
    const back = mulBp(dealt, idiv(BARBARIAN.fury.drinkBp * fury, BARBARIAN.fury.maxBp));
    if (back > 0) healHero(s, h, back, h.pid);
  }
  // A thirsty bloodletting drinks back part of every blow while it lasts.
  if ((h.bleedOn ?? 0) > 0 && h.thirstBp && dealt > 0) healHero(s, h, mulBp(dealt, h.thirstBp), h.pid);
};

/**
 * A barbarian in a war-cry frenzy near this hero, or a cleric's inspiration, quickens its blows (bp of attack
 * speed; 10000 = none). They don't add up: the stronger counts.
 */
const warpathBp = (s: BattleState, h: Hero): number => {
  let bp = (h.inspire ?? 0) > 0 ? CLERIC.inspire.attackSpeedBp : 10000;
  // The foreman's pelt's war drum (docs/PANOPLIA.md §10).
  if ((h.drum ?? 0) > 0) bp = imax(bp, UNIQUE_FORMS.foremanPelt.final.fx.attackSpeedBp as number);
  for (const o of s.heroes) {
    if (o.cls !== 'barbarian' || o.downTicks > 0 || (o.frenzy ?? 0) <= 0 || o.forks?.[2] !== 'warpath') continue;
    if (withinRange(o.x, o.y, h.x, h.y, FORKS.warpath.radius)) return imax(bp, FORKS.warpath.attackSpeedBp);
  }
  return bp;
};

/** Each kill in a bloodlust frenzy draws it out, up to the cap. */
export const bloodlust = (s: BattleState, pid: string): void => {
  for (const h of s.heroes) {
    if (h.pid !== pid || h.cls !== 'barbarian' || (h.frenzy ?? 0) <= 0 || h.forks?.[2] !== 'bloodlust') continue;
    const r = h.ranks?.[2] ?? 1;
    const full = mulBp(SKILLS.frenzy.duration, 10000 + (r - 1) * SKILL_RANK_BP) + FORKS.bloodlust.max;
    h.frenzy = imin(full, (h.frenzy ?? 0) + FORKS.bloodlust.perKill);
  }
};

// ---------- the stats a kit changes while it lasts ----------

/**
 * The hero's stats from the ones the battle gave it, with whatever is running now: a frenzy (quicker,
 * faster), the bear (life, armor, claws in melee), a war cry nearby (quicker). Called every tick.
 */
export const refreshStats = (s: BattleState, h: Hero): void => {
  const war = warpathBp(s, h);
  const frenzy = (h.frenzy ?? 0) > 0;
  const bear = (h.bear ?? 0) > 0;
  const drums = !!s.drums && s.started && s.waveT < (typeof s.drums === 'number' ? s.drums : HALL.drumsTicks);
  if (!frenzy && !bear && war === 10000 && !drums && !h.base) return;
  const b = (h.base ??= { ...h.stats });
  const st = { ...b };
  if (frenzy) {
    st.reload = idiv(b.reload * 10000, BARBARIAN.frenzy.attackSpeedBp);
    st.speed = mulBp(b.speed, BARBARIAN.frenzy.speedBp);
  }
  if (war !== 10000) st.reload = idiv(st.reload * 10000, war);
  if (drums) st.speed = mulBp(st.speed, HALL.drumsBp);
  if (bear) {
    const r = h.ranks?.[1] ?? 1;
    const dire = h.forks?.[1] === 'direBear';
    st.maxHp = mulBp(mulBp(b.maxHp, DRUID.bear.hpBp), dire ? FORKS.direBear.hpBp : 10000);
    st.armorBp = b.armorBp + DRUID.bear.armorBp;
    st.range = DRUID.bear.range;
    st.attack = mulBp(SKILLS.bearForm.damage, 10000 + (r - 1) * SKILL_RANK_BP);
    st.splash = SKILLS.bearForm.radius;
  }
  if (st.reload < 1) st.reload = 1;
  h.stats = st;
  if (!frenzy && !bear && war === 10000 && !drums) h.base = undefined;
};

/** Enters or leaves the bear: life keeps its share of the new maximum. */
export const shiftBear = (s: BattleState, h: Hero, ticks: number): void => {
  const before = h.stats.maxHp;
  h.bear = ticks;
  refreshStats(s, h);
  if (before > 0) h.hp = idiv(h.hp * h.stats.maxHp, before);
  if (h.hp < 1) h.hp = 1;
  if (h.hp > h.stats.maxHp) h.hp = h.stats.maxHp;
};

/** Whether a hero fights in melee right now (the bear's claws too). */
export const isMelee = (h: Hero): boolean => h.cls === 'warrior' || h.cls === 'barbarian' || h.cls === 'paladin' || h.stats.melee === true || (h.bear ?? 0) > 0;

// ---------- paladin ----------

/** The rank (1..3) of a paladin's aura: it grows with the hero's level as the skills do. */
export const auraRank = (p: Hero): number => rankAt(p.level);

/** Devotion: a hero near a standing paladin takes less (one aura only; the highest rank counts). */
export const devotionBp = (s: BattleState, h: Hero): number => {
  let bp = 10000;
  for (const o of s.heroes) {
    // The inherited oath (the paladin's legacy): devotion as a second aura, at its share.
    const second = o.lgAura === 'devotion';
    if (o.cls !== 'paladin' || (o.aura && !second) || o.downTicks > 0 || !withinRange(o.x, o.y, h.x, h.y, PALADIN.devotion.radius)) continue;
    const rank = PALADIN.auraRanks.devotionTakenBp[auraRank(o) - 1] as number;
    bp = imin(bp, second ? secondDevotionBp(rank) : rank);
    if (second) tally(s, 'devotionSecond');
  }
  return bp;
};

/** Retribution: a nightmare that strikes a hero near a paladin carrying it is struck back with light (once, however many). */
export const retribution = (s: BattleState, h: Hero, from: Enemy): void => {
  const a = PALADIN.auras.retribution;
  const near = (o: Hero): boolean => o.cls === 'paladin' && o.downTicks === 0 && withinRange(o.x, o.y, h.x, h.y, a.radius);
  let p = s.heroes.find((o) => o.aura === 'retribution' && near(o));
  // The inherited oath: retribution as a second aura, at its share (a whole one counts first).
  const share = !p && s.lg ? s.heroes.find((o) => o.lgAura === 'retribution' && near(o)) : undefined;
  if (share) p = share;
  if (p && from.hp > 0) {
    const dmg = mulBp(mulBp(mulBp(mulBp(a.damage, PALADIN.auraRanks.retributionBp[auraRank(p) - 1] as number), skillLevelBp(p.level)), p.stats.powerBp), 10000 + (p.stats.damageBp ?? 0));
    const dealt = damageEnemy(s, from, share ? imax(1, secondShare(dmg)) : dmg, { pid: p.pid, ignoresArmor: true, type: 'holy' });
    if (share) tally(s, 'retributionSecond', dealt);
  }
};

/** Life: heroes near a paladin carrying it heal a share of their life every pulse (once, however many). */
export const lifeAura = (s: BattleState): void => {
  const a = PALADIN.auras.life;
  if (s.tick % a.every !== 0) return;
  const ps = s.heroes.filter((o) => o.cls === 'paladin' && (o.aura === 'life' || o.lgAura === 'life') && o.downTicks === 0);
  if (ps.length === 0) return;
  for (const h of s.heroes) {
    if (h.downTicks > 0) continue;
    // A whole aura first; the inherited oath's second at its share.
    const p = ps.find((o) => o.aura === 'life' && withinRange(o.x, o.y, h.x, h.y, a.radius)) ?? ps.find((o) => withinRange(o.x, o.y, h.x, h.y, a.radius));
    if (!p) continue;
    const heal = mulBp(h.stats.maxHp, mulBp(a.hpBp, PALADIN.auraRanks.lifeBp[auraRank(p) - 1] as number));
    const got = healHero(s, h, (p.aura === 'life' ? heal : secondShare(heal)) || 1, p.pid);
    if (p.aura !== 'life') tally(s, 'lifeSecond', got);
  }
};

/** Keep guard: a standing paladin by the keep spares it part of every blow. */
export const keepGuardBp = (s: BattleState): number =>
  s.heroes.some((o) => o.cls === 'paladin' && o.downTicks === 0 && withinRange(o.x, o.y, 0, 0, PALADIN.keepGuard.radius)) ? PALADIN.keepGuard.keepTakenBp : 10000;

/** The ones the paladin's light bites harder: elites, warlords, bosses, the inked and the undead. */
export const corrupt = (e: Enemy): boolean => e.elite || e.un !== undefined || e.tier >= 2 || isBoss(e) || e.kind === 'warden' || e.kind === 'magmaLord' || e.kind === 'colossus' || UNDEAD.includes(e.kind);

/** An aegis soaks what it can of a blow, striking back what it soaked if it was raised in reprisal; returns what gets through. */
export const soak = (s: BattleState, o: { shield?: number; shieldTicks?: number; shieldBack?: boolean }, dmg: number, from?: Enemy | null): number => {
  if (!o.shield || o.shield <= 0 || !o.shieldTicks || o.shieldTicks <= 0 || dmg <= 0) return dmg;
  const took = dmg < o.shield ? dmg : o.shield;
  o.shield -= took;
  if (o.shield <= 0) {
    o.shield = 0;
    o.shieldTicks = 0;
  }
  if (o.shieldBack && from && from.hp > 0) damageEnemy(s, from, took, { pid: '', ignoresArmor: true });
  return dmg - took;
};

/** Aegis on structures and on the keep run out (the heroes' run out with their other timers). */
export const updateShields = (s: BattleState): void => {
  for (const st of s.structures) if (st.shieldTicks && st.shieldTicks > 0 && --st.shieldTicks === 0) st.shield = 0;
  if (s.keepShieldTicks && s.keepShieldTicks > 0 && --s.keepShieldTicks === 0) s.keepShield = 0;
};

/** Raises an aegis on a hero or a structure (the stronger one stays). */
export const raiseShield = (o: { shield?: number; shieldTicks?: number; shieldBack?: boolean }, amount: number, ticks: number, back: boolean): void => {
  if ((o.shield ?? 0) < amount || !o.shieldTicks || o.shieldTicks <= 0) o.shield = amount;
  if ((o.shieldTicks ?? 0) < ticks) o.shieldTicks = ticks;
  o.shieldBack = back;
};

/** Whether a nightmare stands on hallowed ground (it hits softer there). */
export const hallowed = (s: BattleState, e: Enemy): boolean => s.zones.some((z) => z.kind === 'hallow' && withinRange(z.x, z.y, e.x, e.y, z.radius));

/** A nightmare's blow, softened on hallowed ground. */
export const blowOf = (s: BattleState, e: Enemy, dmg: number): number => {
  // Under a bannerman's banner it hits harder.
  if (e.inspired === s.tick || (e.rallied ?? 0) > s.tick) dmg = mulBp(dmg, 10000 + (NIGHTMARES.bannerman.banner?.damageBp ?? 0));
  // Blinded by a paladin's light.
  if ((e.blind ?? 0) > 0 && e.blindBp) dmg = mulBp(dmg, e.blindBp);
  return hallowed(s, e) ? mulBp(dmg, PALADIN.hallow.weakBp) : dmg;
};

// ---------- class buildings in a fight ----------

/**
 * The best level of a standing temple whose reach covers a point (0: none). On terraced land its reach and
 * bonus grow with its level (LAND_LEVELS); on the ring every temple counts as level 1.
 */
const templeAt = (s: BattleState, x: number, y: number): number => {
  let best = 0;
  for (const st of s.structures) {
    if (st.type !== 'temple' || st.destroyed) continue;
    const lv = s.map.land ? st.level : 1;
    const r = s.map.land ? (LAND_LEVELS.temple.range[lv - 1] ?? TEMPLE.range) : TEMPLE.range;
    const c = hexToWorld(st.q, st.r);
    if (lv > best && withinRange(c.x, c.y, x, y, r)) best = lv;
  }
  return best;
};

/** The temple: towers near it hit the inked and the undead harder. */
export const templeBp = (s: BattleState, st: Structure, e: Enemy): number => {
  if (!corrupt(e)) return 10000;
  const c = hexToWorld(st.q, st.r);
  const lv = templeAt(s, c.x, c.y);
  if (lv === 0) return 10000;
  return s.map.land ? (LAND_LEVELS.temple.towerBp[lv - 1] ?? TEMPLE.towerBp) : TEMPLE.towerBp;
};

/** The temple, with a paladin in the fight: structures near it take less. */
export const templeGuardBp = (s: BattleState, st: Structure): number => {
  if (st.type === 'temple' || !s.heroes.some((h) => h.cls === 'paladin' && h.downTicks === 0)) return 10000;
  const c = hexToWorld(st.q, st.r);
  return templeAt(s, c.x, c.y) > 0 ? TEMPLE.takenBp : 10000;
};

/**
 * What a hero's blow brings with it once it has landed (in melee or by a shot): bloodletting's bleeding, the
 * choir's heal, the atonement's, and the duel's drink.
 */
export const heroHitExtras = (s: BattleState, h: Hero, e: Enemy, dealt: number): void => {
  if (dealt <= 0) return;
  if ((h.bleedOn ?? 0) > 0 && e.hp > 0) {
    const f = SKILL_FX.bloodletting;
    e.dotDmg = mulBp(mulBp(f.damage, skillLevelBp(h.level)), h.stats.powerBp);
    e.dotType = 'physical';
    e.dotEvery = f.every;
    e.dotTicks = f.ticks;
    e.lastPid = h.pid;
  }
  if ((h.choir ?? 0) > 0 && h.choirBp) {
    const got = healHero(s, h, mulBp(dealt, h.choirBp), h.pid);
    // The infirmary bell's choir (docs/PANOPLIA.md §10): what it heals counts, and lengthens it.
    if (got > 0 && h.uqT?.choirBy !== undefined) choirHealed(s, h, got);
  }
  let atoned = -1;
  if ((h.atone ?? 0) > 0 && h.atoneBp) {
    h.atone = 0;
    const heal = mulBp(dealt, h.atoneBp);
    atoned = heal;
    if (h.atoneShared) {
      const f = FORKS.sharedAtone;
      for (const o of s.heroes) if (o.downTicks === 0 && withinRange(h.x, h.y, o.x, o.y, f.radius)) healHero(s, o, mulBp(heal, f.bp), h.pid);
    } else {
      let best: Hero | null = null;
      for (const o of s.heroes) {
        if (o.downTicks > 0 || !withinRange(h.x, h.y, o.x, o.y, SKILLS.atone.radius)) continue;
        if (!best || o.hp * best.stats.maxHp < best.hp * o.stats.maxHp) best = o;
      }
      if (best) healHero(s, best, heal, h.pid);
    }
  }
  if (e.duelBy === h.id && (e.duelTicks ?? 0) > 0 && e.duelLeechBp) healHero(s, h, mulBp(dealt, e.duelLeechBp), h.pid);
  // The panoply (docs/PANOPLIA.md §10): the fury's spirits, the fang's heavy blood, the penitence's chain, a blessing's share.
  if (h.uq?.length || h.blessBy !== undefined) panoplyHit(s, h, e, dealt, atoned);
};

/** How hard a hero's blow on this nightmare lands for what it carries (hidden shot, blessed weapon, a duel), in bp. */
export const heroBlowBp = (h: Hero, e: Enemy): number => {
  let bp = 10000;
  if ((h.hidden ?? 0) > 0 && h.hiddenBp) bp = mulBp(bp, h.hiddenBp);
  if ((h.blessed ?? 0) > 0 && h.blessBp) bp = mulBp(bp, h.blessBp);
  if (e.duelBy === h.id && (e.duelTicks ?? 0) > 0) bp = mulBp(bp, SKILL_FX.duel.damageBp);
  // The atoning blow lands harder too.
  if ((h.atone ?? 0) > 0) bp = mulBp(bp, SKILL_FX.atone.blowBp);
  return bp;
};

/** A structure within a paladin's holy rampart: what it takes (bp; 10000 = none). */
export const rampartBp = (s: BattleState, st: Structure): number => {
  let bp = 10000;
  for (const z of s.zones) {
    if ((z.kind !== 'rampart' && z.kind !== 'fortify') || !z.coverBp || z.coverBp >= bp) continue;
    const c = hexToWorld(st.q, st.r);
    if (withinRange(z.x, z.y, c.x, c.y, z.radius)) bp = z.coverBp;
  }
  return bp;
};

/** The grove's roots slow the ground nightmares passing by (a pulse a second), harder with a druid in the fight. */
export const groveRoots = (s: BattleState, st: Structure): void => {
  if (st.type !== 'grove' || st.destroyed || s.tick % 12 !== 0) return;
  const c = hexToWorld(st.q, st.r);
  for (const e of enemiesNear(s, c.x, c.y, GROVE.range, (o) => o.hp > 0 && !isFlyingNow(o))) {
    const bp = (s.heroes.some((h) => h.cls === 'druid' && h.downTicks === 0) ? GROVE.druidSlowBp : GROVE.slowBp) + (s.map.land ? (LAND_LEVELS.grove.slowAddBp[st.level - 1] ?? 0) : 0);
    if (e.slowBp < bp) e.slowBp = bp;
    if (e.slowTicks < 13) e.slowTicks = 13;
  }
};

// ---------- druid ----------

/** Root: a druid standing still heals a little every second. */
export const root = (s: BattleState, h: Hero): void => {
  if (h.cls !== 'druid') return;
  if (h.moving || (h.bear ?? 0) > 0) {
    h.still = 0;
    return;
  }
  h.still = (h.still ?? 0) + 1;
  if (h.still >= DRUID.root.stillTicks && s.tick % DRUID.root.every === 0) healHero(s, h, mulBp(h.stats.maxHp, DRUID.root.hpBp) || 1, h.pid);
};

/** The bear's claws: the blow also lands around its target; a wild heart heals from it. */
export const clawsAround = (s: BattleState, h: Hero, target: Enemy, dmg: number, dealt: number): void => {
  let total = dealt;
  for (const o of enemiesNear(s, target.x, target.y, h.stats.splash, (x) => x.id !== target.id && targetable(s, x))) total += damageEnemy(s, o, dmg, { pid: h.pid });
  if (h.forks?.[1] === 'wildHeart' && total > 0) {
    const heal = mulBp(total, FORKS.wildHeart.healBp);
    for (const o of s.heroes) if (o.downTicks === 0 && withinRange(h.x, h.y, o.x, o.y, FORKS.wildHeart.radius)) healHero(s, o, heal, h.pid);
  }
};


